import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/lib/context/ThemeContext';
import { useToast } from '@/components/ui/use-toast';
import { useTokenRefresh } from '@/lib/api/hooks/useTokenRefresh';
import { ErrorBoundary } from '@/components/ErrorBoundary';

// Loading component for Suspense fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen" data-testid="page-content">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Redirect component that preserves sub-paths from /integration-portal/* to /officer/integrations/*
function IntegrationPortalRedirect() {
  const params = useParams();
  const subPath = params['*'] || '';
  return <Navigate to={`/officer/integrations${subPath ? `/${subPath}` : ''}`} replace />;
}

// Retry wrapper for lazy imports - handles stale chunks after deployments
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((error: Error) => {
      const lastReload = sessionStorage.getItem('chunk_fail_reload');
      const now = Date.now();
      // If we haven't reloaded recently (within 10s), reload to get new manifest
      if (!lastReload || now - Number(lastReload) > 10000) {
        sessionStorage.setItem('chunk_fail_reload', String(now));
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    }),
  );
}

// Layouts - lazy loaded to avoid pulling heavy vendor deps on public pages
const ApplicantLayout = lazyWithRetry(() =>
  import('@/components/layout/ApplicantLayout').then((m) => ({ default: m.ApplicantLayout })),
);
const OfficerLayout = lazyWithRetry(() =>
  import('@/components/layout/OfficerLayout').then((m) => ({ default: m.OfficerLayout })),
);

// ============================================
// Lazy-loaded Route Components
// ============================================

// Public Pages
const PortalSelectionPage = lazyWithRetry(() =>
  import('@/routes/PortalSelectionPage').then((m) => ({ default: m.PortalSelectionPage })),
);
const LoginPage = lazyWithRetry(() =>
  import('@/routes/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const SsoCallbackPage = lazyWithRetry(() =>
  import('@/routes/auth/callback').then((m) => ({ default: m.SsoCallbackPage })),
);

// Officer Dashboard & Core
const OfficerDashboard = lazyWithRetry(() =>
  import('@/routes/OfficerDashboard').then((m) => ({ default: m.OfficerDashboard })),
);
const DashboardPage = lazyWithRetry(() =>
  import('@/routes/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const CustomizableDashboardPage = lazyWithRetry(() =>
  import('@/routes/CustomizableDashboardPage').then((m) => ({
    default: m.CustomizableDashboardPage,
  })),
);
const SettingsPage = lazyWithRetry(() =>
  import('@/routes/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

// Applications
const ApplicationsPage = lazyWithRetry(() =>
  import('@/routes/ApplicationsPage').then((m) => ({ default: m.ApplicationsPage })),
);
const ApplicationDetailPage = lazyWithRetry(() =>
  import('@/routes/ApplicationDetailPage').then((m) => ({ default: m.ApplicationDetailPage })),
);
const NewApplicationPage = lazyWithRetry(() =>
  import('@/routes/NewApplicationPage').then((m) => ({ default: m.NewApplicationPage })),
);
const ApplicationConfigPage = lazyWithRetry(() =>
  import('@/routes/ApplicationConfigPage').then((m) => ({ default: m.ApplicationConfigPage })),
);

// Borrowers
const NotificationsPage = lazyWithRetry(() =>
  import('@/routes/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const BorrowersPage = lazyWithRetry(() =>
  import('@/routes/BorrowersPage').then((m) => ({ default: m.BorrowersPage })),
);
const BorrowerProfilePage = lazyWithRetry(() =>
  import('@/routes/BorrowerProfilePage').then((m) => ({ default: m.BorrowerProfilePage })),
);
const NewBorrowerPage = lazyWithRetry(() =>
  import('@/routes/NewBorrowerPage').then((m) => ({ default: m.NewBorrowerPage })),
);
const EditBorrowerPage = lazyWithRetry(() =>
  import('@/routes/EditBorrowerPage').then((m) => ({ default: m.EditBorrowerPage })),
);
const BorrowerCompliancePage = lazyWithRetry(() =>
  import('@/routes/BorrowerCompliancePage').then((m) => ({ default: m.BorrowerCompliancePage })),
);
const NewSovereignBorrowerPage = lazyWithRetry(() =>
  import('@/routes/NewSovereignBorrowerPage').then((m) => ({
    default: m.NewSovereignBorrowerPage,
  })),
);

// Transactions & Expenses
const TransactionsPage = lazyWithRetry(() =>
  import('@/routes/TransactionsPage').then((m) => ({ default: m.TransactionsPage })),
);
const NewTransactionPage = lazyWithRetry(() =>
  import('@/routes/NewTransactionPage').then((m) => ({ default: m.NewTransactionPage })),
);
const TransactionDetailPage = lazyWithRetry(() =>
  import('@/routes/TransactionDetailPage').then((m) => ({ default: m.TransactionDetailPage })),
);
const ExpensesPage = lazyWithRetry(() =>
  import('@/routes/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
);
const NewExpensePage = lazyWithRetry(() =>
  import('@/routes/NewExpensePage').then((m) => ({ default: m.NewExpensePage })),
);

// Team & Performance
const TeamHierarchyPage = lazyWithRetry(() =>
  import('@/routes/TeamHierarchyPage').then((m) => ({ default: m.TeamHierarchyPage })),
);
const LeaderboardPage = lazyWithRetry(() =>
  import('@/routes/LeaderboardPage').then((m) => ({ default: m.LeaderboardPage })),
);

// Underwriting
const UnderwritingQueuePage = lazyWithRetry(() =>
  import('@/routes/UnderwritingQueuePage').then((m) => ({ default: m.UnderwritingQueuePage })),
);

// Applicant Portal
const ApplicantDashboard = lazyWithRetry(() =>
  import('@/routes/applicant/ApplicantDashboard').then((m) => ({ default: m.ApplicantDashboard })),
);
const MyLoansPage = lazyWithRetry(() =>
  import('@/routes/applicant').then((m) => ({ default: m.MyLoansPage })),
);
const ApplicantPaymentsPage = lazyWithRetry(() =>
  import('@/routes/applicant').then((m) => ({ default: m.ApplicantPaymentsPage })),
);
const ApplicantDocumentsPage = lazyWithRetry(() =>
  import('@/routes/applicant').then((m) => ({ default: m.ApplicantDocumentsPage })),
);
const ProfilePage = lazyWithRetry(() =>
  import('@/routes/applicant').then((m) => ({ default: m.ProfilePage })),
);

// Tasks & Workflows
const MyTasksPage = lazyWithRetry(() => import('@/routes/MyTasksPage'));
const WorkflowConfigPage = lazyWithRetry(() => import('@/routes/WorkflowConfigPage'));

// ESG & Compliance
const ESGPage = lazyWithRetry(() =>
  import('@/routes/ESGPage').then((m) => ({ default: m.ESGPage })),
);
const CollectionsPage = lazyWithRetry(() =>
  import('@/routes/CollectionsPage').then((m) => ({ default: m.CollectionsPage })),
);
const ComplianceDashboardPage = lazyWithRetry(() =>
  import('@/routes/ComplianceDashboardPage').then((m) => ({ default: m.ComplianceDashboardPage })),
);
const ComplianceAlertsPage = lazyWithRetry(() =>
  import('@/routes/ComplianceAlertsPage').then((m) => ({ default: m.ComplianceAlertsPage })),
);

// Servicing - Prepayments
const PrepaymentPage = lazyWithRetry(() =>
  import('@/routes/PrepaymentPage').then((m) => ({ default: m.PrepaymentPage })),
);
const PrepaymentDetailPage = lazyWithRetry(() =>
  import('@/routes/PrepaymentPage').then((m) => ({ default: m.PrepaymentDetailPage })),
);
const PrepaymentSimulatorPage = lazyWithRetry(() =>
  import('@/routes/PrepaymentSimulatorPage').then((m) => ({ default: m.PrepaymentSimulatorPage })),
);
const PrepaymentApprovalPage = lazyWithRetry(() =>
  import('@/routes/admin/PrepaymentApprovalPage').then((m) => ({
    default: m.PrepaymentApprovalPage,
  })),
);

// Servicing - Restructuring
const RestructuringListPage = lazyWithRetry(() =>
  import('@/routes/RestructuringListPage').then((m) => ({ default: m.RestructuringListPage })),
);
const RestructuringDetailPage = lazyWithRetry(() =>
  import('@/routes/RestructuringDetailPage').then((m) => ({ default: m.RestructuringDetailPage })),
);
const CreateRestructuringPage = lazyWithRetry(() =>
  import('@/routes/CreateRestructuringPage').then((m) => ({ default: m.CreateRestructuringPage })),
);

// FX Hedging
const FxHedgingListPage = lazyWithRetry(() =>
  import('@/routes/FxHedgingListPage').then((m) => ({ default: m.FxHedgingListPage })),
);
const FxHedgingDetailPage = lazyWithRetry(() =>
  import('@/routes/FxHedgingDetailPage').then((m) => ({ default: m.FxHedgingDetailPage })),
);
const CreateFxHedgePage = lazyWithRetry(() =>
  import('@/routes/CreateFxHedgePage').then((m) => ({ default: m.CreateFxHedgePage })),
);
const FxExposurePage = lazyWithRetry(() =>
  import('@/routes/FxExposurePage').then((m) => ({ default: m.FxExposurePage })),
);
const FxRevaluationPage = lazyWithRetry(() =>
  import('@/routes/FxRevaluationPage').then((m) => ({ default: m.FxRevaluationPage })),
);
const FxRateSettingsPage = lazyWithRetry(() =>
  import('@/routes/FxRateSettingsPage').then((m) => ({ default: m.FxRateSettingsPage })),
);
const FxRateHistoryPage = lazyWithRetry(() =>
  import('@/routes/FxRateHistoryPage').then((m) => ({ default: m.FxRateHistoryPage })),
);

// EOD Processing
const EODDashboardPage = lazyWithRetry(() =>
  import('@/routes/EODDashboardPage').then((m) => ({ default: m.EODDashboardPage })),
);
const BatchProcessingPage = lazyWithRetry(() =>
  import('@/routes/BatchProcessingPage').then((m) => ({ default: m.BatchProcessingPage })),
);
const GLAccountingPage = lazyWithRetry(() =>
  import('@/routes/GLAccountingPage').then((m) => ({ default: m.GLAccountingPage })),
);
const GLPostingPage = lazyWithRetry(() =>
  import('@/routes/GLPostingPage').then((m) => ({ default: m.GLPostingPage })),
);
const WriteOffsPage = lazyWithRetry(() =>
  import('@/routes/WriteOffsPage').then((m) => ({ default: m.WriteOffsPage })),
);
const WriteOffDetailPage = lazyWithRetry(() =>
  import('@/routes/WriteOffDetailPage').then((m) => ({ default: m.WriteOffDetailPage })),
);

// Documents
const DocumentsPage = lazyWithRetry(() =>
  import('@/routes/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
);
const DocumentViewerPage = lazyWithRetry(() =>
  import('@/routes/DocumentViewerPage').then((m) => ({ default: m.DocumentViewerPage })),
);

// Admin
const AdminDashboardPage = lazyWithRetry(() =>
  import('@/routes/admin').then((m) => ({ default: m.AdminDashboardPage })),
);
const RolesPermissionsPage = lazyWithRetry(() =>
  import('@/routes/admin').then((m) => ({ default: m.RolesPermissionsPage })),
);
const SystemSettingsPage = lazyWithRetry(() =>
  import('@/routes/admin').then((m) => ({ default: m.SystemSettingsPage })),
);
const ComplianceAdminPage = lazyWithRetry(() =>
  import('@/routes/admin').then((m) => ({ default: m.ComplianceAdminPage })),
);
const AgentConfigPage = lazyWithRetry(() =>
  import('@/routes/admin').then((m) => ({ default: m.AgentConfigPage })),
);
const DeliverySettingsPage = lazyWithRetry(() =>
  import('@/routes/admin').then((m) => ({ default: m.DeliverySettingsPage })),
);
const UserManagementPage = lazyWithRetry(() =>
  import('@/routes/UserManagementPage').then((m) => ({ default: m.UserManagementPage })),
);
const AuditLogPage = lazyWithRetry(() =>
  import('@/routes/AuditLogPage').then((m) => ({ default: m.AuditLogPage })),
);
const OrganizationManagementPage = lazyWithRetry(() =>
  import('@/routes/OrganizationManagementPage').then((m) => ({
    default: m.OrganizationManagementPage,
  })),
);
const OrganizationSettingsPage = lazyWithRetry(() =>
  import('@/routes/OrganizationSettingsPage').then((m) => ({
    default: m.OrganizationSettingsPage,
  })),
);

// Lending Portal
const LendingPortalPage = lazyWithRetry(() =>
  import('@/routes/LendingPortalPage').then((m) => ({ default: m.LendingPortalPage })),
);
const LoanAccountsPage = lazyWithRetry(() =>
  import('@/routes/LoanAccountsPage').then((m) => ({ default: m.LoanAccountsPage })),
);
const LoanAccountDetailPage = lazyWithRetry(() =>
  import('@/routes/LoanAccountDetailPage').then((m) => ({ default: m.LoanAccountDetailPage })),
);
const RecordPaymentPage = lazyWithRetry(() =>
  import('@/routes/RecordPaymentPage').then((m) => ({ default: m.RecordPaymentPage })),
);
const PortfolioPage = lazyWithRetry(() =>
  import('@/routes/PortfolioPage').then((m) => ({ default: m.PortfolioPage })),
);
const PaymentsPage = lazyWithRetry(() =>
  import('@/routes/PaymentsPage').then((m) => ({ default: m.PaymentsPage })),
);
const DisbursementsPage = lazyWithRetry(() =>
  import('@/routes/DisbursementsPage').then((m) => ({ default: m.DisbursementsPage })),
);
const InterestPage = lazyWithRetry(() =>
  import('@/routes/InterestPage').then((m) => ({ default: m.InterestPage })),
);
const CovenantsPage = lazyWithRetry(() =>
  import('@/routes/CovenantsPage').then((m) => ({ default: m.CovenantsPage })),
);
const CurrencyManagementPage = lazyWithRetry(() =>
  import('@/routes/CurrencyManagementPage').then((m) => ({ default: m.CurrencyManagementPage })),
);

// Facilities
const FacilitiesPage = lazyWithRetry(() =>
  import('@/routes/facilities').then((m) => ({ default: m.FacilitiesPage })),
);
const FacilityDetailPage = lazyWithRetry(() =>
  import('@/routes/facilities').then((m) => ({ default: m.FacilityDetailPage })),
);
const NewFacilityPage = lazyWithRetry(() =>
  import('@/routes/facilities').then((m) => ({ default: m.NewFacilityPage })),
);

// Documentation
const DocumentationPage = lazyWithRetry(() => import('@/routes/DocumentationPage'));
const GettingStartedDoc = lazyWithRetry(() => import('@/routes/docs/GettingStartedDoc'));
const EodOverviewDoc = lazyWithRetry(() => import('@/routes/docs/EodOverviewDoc'));
const PricingDoc = lazyWithRetry(() => import('@/routes/docs/PricingDoc'));
const ApiOverviewDoc = lazyWithRetry(() => import('@/routes/docs/ApiOverviewDoc'));
const AuthenticationDoc = lazyWithRetry(() => import('@/routes/docs/AuthenticationDoc'));
const ConnectorsDoc = lazyWithRetry(() => import('@/routes/docs/ConnectorsDoc'));
const LoanOriginationDoc = lazyWithRetry(() => import('@/routes/docs/LoanOriginationDoc'));
const ComplianceDoc = lazyWithRetry(() => import('@/routes/docs/ComplianceDoc'));
const UnderwritingDoc = lazyWithRetry(() => import('@/routes/docs/UnderwritingDoc'));
const ServicingDoc = lazyWithRetry(() => import('@/routes/docs/ServicingDoc'));
const BorrowerManagementDoc = lazyWithRetry(() => import('@/routes/docs/BorrowerManagementDoc'));
const DocumentManagementDoc = lazyWithRetry(() => import('@/routes/docs/DocumentManagementDoc'));
const UBODoc = lazyWithRetry(() => import('@/routes/docs/UBODoc'));
const DisbursementDoc = lazyWithRetry(() => import('@/routes/docs/DisbursementDoc'));
const StorageConfigurationDoc = lazyWithRetry(
  () => import('@/routes/docs/StorageConfigurationDoc'),
);
const AIAgentDoc = lazyWithRetry(() => import('@/routes/docs/AIAgentDoc'));

// Version History
const VersionHistoryPage = lazyWithRetry(() =>
  import('@/routes/VersionHistoryPage').then((m) => ({ default: m.VersionHistoryPage })),
);

// Pricing Module
const PricingDashboardPage = lazyWithRetry(() => import('@/routes/PricingDashboardPage'));
const LoanPricingCalculatorPage = lazyWithRetry(() => import('@/routes/LoanPricingCalculatorPage'));
const ScenarioSimulatorPage = lazyWithRetry(() => import('@/routes/ScenarioSimulatorPage'));
const PricingHistoryPage = lazyWithRetry(() => import('@/routes/PricingHistoryPage'));
const PricingDetailPage = lazyWithRetry(() => import('@/routes/PricingDetailPage'));
const RateSheetManagerPage = lazyWithRetry(() => import('@/routes/RateSheetManagerPage'));
const MarketDataPortal = lazyWithRetry(() => import('@/routes/MarketDataPortal'));

// Market Data Portal (Standalone)
const MarketDataDashboard = lazyWithRetry(() => import('@/routes/market-data/MarketDataDashboard'));
const MarketDataExplorer = lazyWithRetry(() => import('@/routes/market-data/MarketDataExplorer'));
const MarketDataCurves = lazyWithRetry(() => import('@/routes/market-data/MarketDataCurves'));
const MarketDataRateDetail = lazyWithRetry(
  () => import('@/routes/market-data/MarketDataRateDetail'),
);
const MarketDataAlerts = lazyWithRetry(() => import('@/routes/market-data/MarketDataAlerts'));
const MarketDataWatchlist = lazyWithRetry(() => import('@/routes/market-data/MarketDataWatchlist'));
const MarketDataCorrelation = lazyWithRetry(
  () => import('@/routes/market-data/MarketDataCorrelation'),
);
const MarketDataAdmin = lazyWithRetry(() => import('@/routes/market-data/MarketDataAdmin'));
const MarketDataComparison = lazyWithRetry(
  () => import('@/routes/market-data/MarketDataComparison'),
);

// Analytics
const RiskDashboardPage = lazyWithRetry(() =>
  import('@/routes/RiskDashboardPage').then((m) => ({ default: m.RiskDashboardPage })),
);
const AdvancedAnalyticsPage = lazyWithRetry(() =>
  import('@/routes/AdvancedAnalyticsPage').then((m) => ({ default: m.AdvancedAnalyticsPage })),
);
const ReportTemplatesPage = lazyWithRetry(() =>
  import('@/routes/analytics').then((m) => ({ default: m.ReportTemplatesPage })),
);
const ReportBuilderPage = lazyWithRetry(() =>
  import('@/routes/analytics').then((m) => ({ default: m.ReportBuilderPage })),
);
const ReportViewerPage = lazyWithRetry(() =>
  import('@/routes/analytics').then((m) => ({ default: m.ReportViewerPage })),
);
const PortfolioAnalyticsPage = lazyWithRetry(() =>
  import('@/routes/analytics').then((m) => ({ default: m.PortfolioAnalyticsPage })),
);

// Collateral Management
const CollateralPage = lazyWithRetry(() =>
  import('@/routes/CollateralPage').then((m) => ({ default: m.CollateralPage })),
);
const CollateralDetailPage = lazyWithRetry(() =>
  import('@/routes/CollateralDetailPage').then((m) => ({ default: m.CollateralDetailPage })),
);

// Document Template Editor
const DocumentTemplatesPage = lazyWithRetry(() =>
  import('@/routes/document-templates').then((m) => ({ default: m.DocumentTemplatesPage })),
);
const DocumentTemplateBuilderPage = lazyWithRetry(() =>
  import('@/routes/document-templates').then((m) => ({ default: m.DocumentTemplateBuilderPage })),
);
const DocumentTemplatePreviewPage = lazyWithRetry(() =>
  import('@/routes/document-templates').then((m) => ({ default: m.DocumentTemplatePreviewPage })),
);
const DocumentTemplateGeneratePage = lazyWithRetry(() =>
  import('@/routes/document-templates').then((m) => ({ default: m.DocumentTemplateGeneratePage })),
);

// IFI Module
const CountryRiskPage = lazyWithRetry(() =>
  import('@/routes/CountryRiskPage').then((m) => ({ default: m.CountryRiskPage })),
);
const HolidayCalendarPage = lazyWithRetry(() => import('@/routes/HolidayCalendarPage'));
const IFICompliancePage = lazyWithRetry(() =>
  import('@/routes/IFICompliancePage').then((m) => ({ default: m.IFICompliancePage })),
);
const IFIReportingPage = lazyWithRetry(() =>
  import('@/routes/IFIReportingPage').then((m) => ({ default: m.IFIReportingPage })),
);
const GuaranteesPage = lazyWithRetry(() =>
  import('@/routes/GuaranteesPage').then((m) => ({ default: m.GuaranteesPage })),
);
const GuaranteeDetailPage = lazyWithRetry(() =>
  import('@/routes/GuaranteeDetailPage').then((m) => ({ default: m.GuaranteeDetailPage })),
);
const GuarantorsPage = lazyWithRetry(() =>
  import('@/routes/GuarantorsPage').then((m) => ({ default: m.GuarantorsPage })),
);
const ProjectsPage = lazyWithRetry(() =>
  import('@/routes/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const NewProjectPage = lazyWithRetry(() =>
  import('@/routes/NewProjectPage').then((m) => ({ default: m.NewProjectPage })),
);
const ProjectDetailPage = lazyWithRetry(() =>
  import('@/routes/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })),
);
const ProjectConfigPage = lazyWithRetry(() =>
  import('@/routes/ProjectConfigPage').then((m) => ({ default: m.ProjectConfigPage })),
);
const ImpactPage = lazyWithRetry(() =>
  import('@/routes/ImpactPage').then((m) => ({ default: m.ImpactPage })),
);
const CofinancingPage = lazyWithRetry(() =>
  import('@/routes/CofinancingPage').then((m) => ({ default: m.CofinancingPage })),
);
const TechnicalAssistancePage = lazyWithRetry(() =>
  import('@/routes/TechnicalAssistancePage').then((m) => ({ default: m.TechnicalAssistancePage })),
);

// CRM Module
const CRMDashboardPage = lazyWithRetry(() =>
  import('@/routes/crm/CRMDashboardPage').then((m) => ({ default: m.CRMDashboardPage })),
);
const AccountsPage = lazyWithRetry(() =>
  import('@/routes/crm/AccountsPage').then((m) => ({ default: m.AccountsPage })),
);
const NewAccountPage = lazyWithRetry(() =>
  import('@/routes/crm/NewAccountPage').then((m) => ({ default: m.NewAccountPage })),
);
const AccountDetailPage = lazyWithRetry(() =>
  import('@/routes/crm/AccountDetailPage').then((m) => ({ default: m.AccountDetailPage })),
);
const ContactsPage = lazyWithRetry(() =>
  import('@/routes/crm/ContactsPage').then((m) => ({ default: m.ContactsPage })),
);
const ContactDetailPage = lazyWithRetry(() =>
  import('@/routes/crm/ContactDetailPage').then((m) => ({ default: m.ContactDetailPage })),
);
const EditContactPage = lazyWithRetry(() =>
  import('@/routes/crm/EditContactPage').then((m) => ({ default: m.EditContactPage })),
);
const NewContactPage = lazyWithRetry(() =>
  import('@/routes/crm/NewContactPage').then((m) => ({ default: m.NewContactPage })),
);
const LeadsPage = lazyWithRetry(() =>
  import('@/routes/crm/LeadsPage').then((m) => ({ default: m.LeadsPage })),
);
const LeadDetailPage = lazyWithRetry(() =>
  import('@/routes/crm/LeadDetailPage').then((m) => ({ default: m.LeadDetailPage })),
);
const NewLeadPage = lazyWithRetry(() =>
  import('@/routes/crm/NewLeadPage').then((m) => ({ default: m.NewLeadPage })),
);
const EditLeadPage = lazyWithRetry(() =>
  import('@/routes/crm/EditLeadPage').then((m) => ({ default: m.EditLeadPage })),
);
const OpportunitiesPage = lazyWithRetry(() =>
  import('@/routes/crm/OpportunitiesPage').then((m) => ({ default: m.OpportunitiesPage })),
);
const OpportunityDetailPage = lazyWithRetry(() =>
  import('@/routes/crm/OpportunityDetailPage').then((m) => ({ default: m.OpportunityDetailPage })),
);
const NewOpportunityPage = lazyWithRetry(() =>
  import('@/routes/crm/NewOpportunityPage').then((m) => ({ default: m.NewOpportunityPage })),
);
const EditOpportunityPage = lazyWithRetry(() =>
  import('@/routes/crm/EditOpportunityPage').then((m) => ({ default: m.EditOpportunityPage })),
);
const OpportunitiesPipelinePage = lazyWithRetry(
  () => import('@/routes/crm/OpportunitiesPipelinePage'),
);
const ActivitiesPage = lazyWithRetry(() =>
  import('@/routes/crm/ActivitiesPage').then((m) => ({ default: m.ActivitiesPage })),
);
const NewActivityPage = lazyWithRetry(() =>
  import('@/routes/crm/NewActivityPage').then((m) => ({ default: m.NewActivityPage })),
);
const ActivityDetailPage = lazyWithRetry(() =>
  import('@/routes/crm/ActivityDetailPage').then((m) => ({ default: m.ActivityDetailPage })),
);
const EditActivityPage = lazyWithRetry(() =>
  import('@/routes/crm/EditActivityPage').then((m) => ({ default: m.EditActivityPage })),
);
const CampaignsPage = lazyWithRetry(() =>
  import('@/routes/crm/CampaignsPage').then((m) => ({ default: m.CampaignsPage })),
);
const NewCampaignPage = lazyWithRetry(() =>
  import('@/routes/crm/NewCampaignPage').then((m) => ({ default: m.NewCampaignPage })),
);
const TermSheetsPage = lazyWithRetry(() =>
  import('@/routes/crm/TermSheetsPage').then((m) => ({ default: m.TermSheetsPage })),
);
const NewTermSheetPage = lazyWithRetry(() =>
  import('@/routes/crm/NewTermSheetPage').then((m) => ({ default: m.NewTermSheetPage })),
);
const TermSheetDetailPage = lazyWithRetry(() =>
  import('@/routes/crm/TermSheetDetailPage').then((m) => ({ default: m.TermSheetDetailPage })),
);
const EditTermSheetPage = lazyWithRetry(() =>
  import('@/routes/crm/EditTermSheetPage').then((m) => ({ default: m.EditTermSheetPage })),
);
const ServiceCasesPage = lazyWithRetry(() =>
  import('@/routes/crm/ServiceCasesPage').then((m) => ({ default: m.ServiceCasesPage })),
);
const NewServiceCasePage = lazyWithRetry(() =>
  import('@/routes/crm/NewServiceCasePage').then((m) => ({ default: m.NewServiceCasePage })),
);
const PipelineStagesPage = lazyWithRetry(() =>
  import('@/routes/crm/PipelineStagesPage').then((m) => ({ default: m.PipelineStagesPage })),
);
const SalesPerformancePage = lazyWithRetry(() =>
  import('@/routes/crm/SalesPerformancePage').then((m) => ({ default: m.SalesPerformancePage })),
);
const SalesForecastPage = lazyWithRetry(() =>
  import('@/routes/crm/SalesForecastPage').then((m) => ({ default: m.SalesForecastPage })),
);
const WinLossAnalysisPage = lazyWithRetry(() => import('@/routes/crm/WinLossAnalysisPage'));
const RevenueForecastingPage = lazyWithRetry(() => import('@/routes/crm/RevenueForecastingPage'));
const CRMSettingsPage = lazyWithRetry(() =>
  import('@/routes/crm/CRMSettingsPage').then((m) => ({ default: m.CRMSettingsPage })),
);

// Integration Portal
const PortalDashboard = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.PortalDashboard })),
);
const APIManagementDashboard = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.APIManagementDashboard })),
);
const DeveloperPortalHome = lazyWithRetry(
  () => import('@/components/integration-portal/developer-portal/DeveloperPortalHome'),
);
const APIDocumentation = lazyWithRetry(
  () => import('@/components/integration-portal/developer-portal/APIDocumentation'),
);
const APIPlayground = lazyWithRetry(
  () => import('@/components/integration-portal/developer-portal/APIPlayground'),
);
const SDKDownloads = lazyWithRetry(
  () => import('@/components/integration-portal/developer-portal/SDKDownloads'),
);
const AnnouncementsManagementPage = lazyWithRetry(
  () => import('@/components/integration-portal/developer-portal/AnnouncementsManagementPage'),
);
const RealtimeMonitor = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.RealtimeMonitor })),
);
const LogViewer = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.LogViewer })),
);
const AlertsPanel = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.AlertsPanel })),
);
const ConnectorMarketplace = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.ConnectorMarketplace })),
);
const ConnectorDetailView = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.ConnectorDetailView })),
);
const CustomConnectorBuilder = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.CustomConnectorBuilder })),
);
const InstalledConnectorsPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.InstalledConnectorsPage })),
);
const IntegrationWorkflows = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.IntegrationWorkflows })),
);
const BusinessProcessWorkflows = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.BusinessProcessWorkflows })),
);
const TriggersPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.TriggersPage })),
);
const ActionsPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.ActionsPage })),
);
const HealthStatusPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.HealthStatusPage })),
);
const ActivityFeedPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.ActivityFeedPage })),
);
const WebhooksPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.WebhooksPage })),
);
const EventLogPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.EventLogPage })),
);
const ExportsPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.ExportsPage })),
);
const SchemasPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.SchemasPage })),
);
const GeneralSettingsPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.GeneralSettingsPage })),
);
const TeamSettingsPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.TeamSettingsPage })),
);
const EnvironmentsPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.EnvironmentsPage })),
);
const EnvironmentVariablesPage = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.EnvironmentVariablesPage })),
);
const ApiKeysPanel = lazyWithRetry(() =>
  import('@/components/integration-portal').then((m) => ({ default: m.ApiKeysPanel })),
);

// ============================================
// Auth Components
// ============================================

// Simple auth check - can be made more sophisticated later
function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

const ADMIN_ROLES = ['SYSTEM_ADMIN', 'ADMIN', 'SUPER_ADMIN'];

// Route guard for admin-only pages
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  try {
    // Decode role from the JWT payload — avoids storing PII in localStorage
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload?.role && !ADMIN_ROLES.includes(payload.role)) {
      return <Navigate to="/officer/dashboard" replace />;
    }
  } catch {
    // If token is malformed, allow access (backend will enforce)
  }

  return children;
}

// Component to handle automatic token refresh when user is active
function TokenRefreshHandler() {
  useTokenRefresh();
  return null;
}

// Component to handle auth expiration events and redirect to login
function AuthExpirationHandler() {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleUnauthorized = () => {
      // Clear access token from localStorage; refreshToken cookie is cleared server-side
      localStorage.removeItem('accessToken');
      localStorage.removeItem('token');

      // Show toast notification
      toast({
        title: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
        variant: 'destructive',
      });

      // Redirect to login
      navigate('/login', { replace: true });
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [navigate, toast]);

  return null;
}

// Placeholder for coming soon pages
function ComingSoon({ title }: { title: string }) {
  return (
    <div className="text-center p-8" data-testid="page-content">
      <h2 className="text-2xl font-bold">{title} - Coming Soon</h2>
      <p className="text-text-caption mt-2">This feature is under development.</p>
    </div>
  );
}

function AppContent() {
  return (
    <>
      <AuthExpirationHandler />
      <TokenRefreshHandler />
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Root redirects to login for unauthenticated users */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<SsoCallbackPage />} />
            <Route path="/portals" element={<PortalSelectionPage />} />
            <Route path="/portal-selection" element={<PortalSelectionPage />} />

            {/* Documentation Portal - Public Access */}
            <Route path="/docs" element={<DocumentationPage />} />
            <Route path="/docs/getting-started/overview" element={<GettingStartedDoc />} />
            <Route path="/docs/getting-started/*" element={<GettingStartedDoc />} />
            <Route path="/docs/eod/overview" element={<EodOverviewDoc />} />
            <Route path="/docs/eod/*" element={<EodOverviewDoc />} />
            <Route path="/docs/pricing/overview" element={<PricingDoc />} />
            <Route path="/docs/pricing/*" element={<PricingDoc />} />
            <Route path="/docs/api/overview" element={<ApiOverviewDoc />} />
            <Route path="/docs/api/auth" element={<AuthenticationDoc />} />
            <Route path="/docs/api/connectors" element={<ConnectorsDoc />} />
            <Route path="/docs/api/*" element={<ApiOverviewDoc />} />
            <Route path="/docs/loan-origination/overview" element={<LoanOriginationDoc />} />
            <Route path="/docs/compliance/overview" element={<ComplianceDoc />} />
            <Route path="/docs/compliance/*" element={<ComplianceDoc />} />
            <Route path="/docs/underwriting/overview" element={<UnderwritingDoc />} />
            <Route path="/docs/underwriting/*" element={<UnderwritingDoc />} />
            <Route path="/docs/servicing/overview" element={<ServicingDoc />} />
            <Route path="/docs/servicing/*" element={<ServicingDoc />} />
            <Route path="/docs/origination/application" element={<LoanOriginationDoc />} />
            <Route path="/docs/origination/borrowers" element={<BorrowerManagementDoc />} />
            <Route path="/docs/origination/documents" element={<DocumentManagementDoc />} />
            <Route path="/docs/origination/ubo" element={<UBODoc />} />
            <Route path="/docs/disbursement/process" element={<DisbursementDoc />} />
            <Route path="/docs/disbursement/*" element={<DisbursementDoc />} />
            <Route path="/docs/admin/storage" element={<StorageConfigurationDoc />} />
            <Route path="/docs/agent/overview" element={<AIAgentDoc />} />
            <Route path="/docs/agent/*" element={<AIAgentDoc />} />
            <Route path="/docs/admin/*" element={<DocumentationPage />} />
            <Route path="/docs/analytics/*" element={<DocumentationPage />} />
            <Route path="/docs/officer/*" element={<DocumentationPage />} />
            <Route path="/docs/*" element={<DocumentationPage />} />

            {/* Applicant Portal - V2.1 */}
            <Route
              path="/applicant"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageLoader />}>
                    <ApplicantLayout />
                  </Suspense>
                </RequireAuth>
              }
            >
              <Route index element={<ApplicantDashboard />} />
              <Route path="dashboard" element={<ApplicantDashboard />} />
              <Route path="apply" element={<NewApplicationPage />} />
              <Route path="applications" element={<ApplicationsPage />} />
              <Route path="applications/:id" element={<ApplicationDetailPage />} />
              {/* Additional applicant routes will be added here */}
              <Route path="loans" element={<MyLoansPage />} />
              <Route path="payments" element={<ApplicantPaymentsPage />} />
              <Route path="documents" element={<ApplicantDocumentsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Market Data Portal - Standalone V5.0 */}
            <Route
              path="/market-data"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageLoader />}>
                    <OfficerLayout />
                  </Suspense>
                </RequireAuth>
              }
            >
              <Route index element={<MarketDataDashboard />} />
              <Route path="explorer" element={<MarketDataExplorer />} />
              <Route path="rates/:rateType" element={<MarketDataRateDetail />} />
              <Route path="curves" element={<MarketDataCurves />} />
              <Route path="watchlists" element={<MarketDataWatchlist />} />
              <Route path="comparison" element={<MarketDataComparison />} />
              <Route path="correlation" element={<MarketDataCorrelation />} />
              <Route path="browser" element={<Navigate to="/market-data/explorer" replace />} />
              <Route path="alerts" element={<MarketDataAlerts />} />
              <Route path="admin" element={<MarketDataAdmin />} />
              <Route
                path="audit"
                element={<Navigate to="/market-data/admin?tab=audit" replace />}
              />
            </Route>

            {/* Officer Portal - V2.0 + V2.1 */}
            <Route
              path="/officer"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageLoader />}>
                    <OfficerLayout />
                  </Suspense>
                </RequireAuth>
              }
            >
              <Route index element={<OfficerDashboard />} />
              <Route path="dashboard" element={<OfficerDashboard />} />
              <Route path="my-dashboard" element={<CustomizableDashboardPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="pipeline" element={<ApplicationsPage />} />
              <Route path="applications" element={<ApplicationsPage />} />
              <Route path="applications/new" element={<NewApplicationPage />} />
              <Route path="applications/config" element={<ApplicationConfigPage />} />
              <Route path="applications/:id" element={<ApplicationDetailPage />} />
              <Route path="applications/:id/config" element={<ApplicationConfigPage />} />
              <Route path="borrowers" element={<BorrowersPage />} />
              <Route path="borrowers/new" element={<NewBorrowerPage />} />
              <Route path="borrowers/new-sovereign" element={<NewSovereignBorrowerPage />} />
              <Route path="borrowers/:id" element={<BorrowerProfilePage />} />
              <Route path="borrowers/:id/edit" element={<EditBorrowerPage />} />
              <Route path="borrowers/:id/compliance" element={<BorrowerCompliancePage />} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="transactions/new" element={<NewTransactionPage />} />
              <Route path="transactions/:id" element={<TransactionDetailPage />} />
              <Route path="team" element={<TeamHierarchyPage />} />
              <Route path="leaderboard" element={<LeaderboardPage />} />
              <Route path="expenses" element={<ExpensesPage />} />
              <Route path="expenses/new" element={<NewExpensePage />} />
              <Route path="tasks" element={<MyTasksPage />} />
              <Route path="workflows" element={<WorkflowConfigPage />} />
              <Route path="esg" element={<ESGPage />} />
              <Route path="collections" element={<CollectionsPage />} />
              {/* Servicing Module */}
              <Route
                path="servicing"
                element={<Navigate to="/officer/servicing/prepayments" replace />}
              />
              <Route path="servicing/prepayments" element={<PrepaymentPage />} />
              <Route path="servicing/prepayments/:id" element={<PrepaymentDetailPage />} />
              {/* Prepayment Simulator Tool */}
              <Route path="tools/prepayment-simulator" element={<PrepaymentSimulatorPage />} />
              <Route path="servicing/restructuring" element={<RestructuringListPage />} />
              <Route path="servicing/restructuring/new" element={<CreateRestructuringPage />} />
              <Route path="servicing/restructuring/:id" element={<RestructuringDetailPage />} />
              <Route
                path="servicing/restructuring/:id/edit"
                element={<CreateRestructuringPage />}
              />
              <Route path="servicing/write-offs" element={<WriteOffsPage />} />
              <Route path="servicing/write-offs/:id" element={<WriteOffDetailPage />} />
              <Route path="servicing/write-offs/:id/audit" element={<WriteOffDetailPage />} />
              {/* FX Hedging */}
              <Route path="fx-hedging" element={<FxHedgingListPage />} />
              <Route path="fx-hedging/new" element={<CreateFxHedgePage />} />
              <Route path="fx-hedging/:id" element={<FxHedgingDetailPage />} />
              {/* FX Revaluation */}
              <Route path="fx-revaluation" element={<FxRevaluationPage />} />
              <Route path="underwriting" element={<UnderwritingQueuePage />} />
              <Route path="compliance" element={<ComplianceDashboardPage />} />
              <Route path="compliance/alerts" element={<ComplianceAlertsPage />} />
              <Route path="eod" element={<EODDashboardPage />} />
              <Route path="eod/batch" element={<BatchProcessingPage />} />
              <Route path="eod/gl" element={<GLAccountingPage />} />
              <Route path="eod/posting" element={<GLPostingPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="documents/:id" element={<DocumentViewerPage />} />
              {/* Document Template Editor */}
              <Route path="documents/templates" element={<DocumentTemplatesPage />} />
              <Route path="documents/templates/builder" element={<DocumentTemplateBuilderPage />} />
              <Route
                path="documents/templates/builder/:id"
                element={<DocumentTemplateBuilderPage />}
              />
              <Route
                path="documents/templates/preview/:id"
                element={<DocumentTemplatePreviewPage />}
              />
              <Route
                path="documents/templates/generate/:id"
                element={<DocumentTemplateGeneratePage />}
              />
              <Route
                path="admin"
                element={
                  <RequireAdmin>
                    <AdminDashboardPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/users"
                element={
                  <RequireAdmin>
                    <UserManagementPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/roles"
                element={
                  <RequireAdmin>
                    <RolesPermissionsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/organizations"
                element={
                  <RequireAdmin>
                    <OrganizationManagementPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/audit-log"
                element={
                  <RequireAdmin>
                    <AuditLogPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/currencies"
                element={
                  <RequireAdmin>
                    <CurrencyManagementPage />
                  </RequireAdmin>
                }
              />
              <Route path="admin/holidays" element={<HolidayCalendarPage />} />
              <Route
                path="admin/settings"
                element={
                  <RequireAdmin>
                    <SystemSettingsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/compliance"
                element={
                  <RequireAdmin>
                    <ComplianceAdminPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/collateral"
                element={
                  <RequireAdmin>
                    <CollateralPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/agent"
                element={
                  <RequireAdmin>
                    <AgentConfigPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/delivery"
                element={
                  <RequireAdmin>
                    <DeliverySettingsPage />
                  </RequireAdmin>
                }
              />
              <Route path="settings/organization" element={<OrganizationSettingsPage />} />

              {/* Legacy integrations route removed - use Integration Portal at /integration-portal */}

              <Route path="lending" element={<LendingPortalPage />} />
              <Route path="lending/portfolio" element={<PortfolioPage />} />
              <Route path="lending/loans" element={<LoanAccountsPage />} />
              <Route path="lending/loans/:id" element={<LoanAccountDetailPage />} />
              <Route path="lending/loan-accounts/:id" element={<LoanAccountDetailPage />} />
              <Route path="lending/payments" element={<PaymentsPage />} />
              <Route path="lending/payments/new" element={<RecordPaymentPage />} />
              <Route path="lending/disbursements" element={<DisbursementsPage />} />
              <Route path="lending/interest" element={<InterestPage />} />
              <Route path="lending/covenants" element={<CovenantsPage />} />
              <Route path="lending/treasury" element={<CurrencyManagementPage />} />
              <Route
                path="lending/schedule"
                element={
                  <div className="text-center p-8" data-testid="page-content">
                    <div data-testid="schedule-content">
                      <h2 className="text-2xl font-bold">Payment Schedule</h2>
                      <p className="text-text-caption mt-2">
                        Tranche disbursement schedules and payment timelines.
                      </p>
                      <table className="mt-6 w-full max-w-2xl mx-auto text-left border">
                        <thead>
                          <tr>
                            <th className="border p-2">Tranche</th>
                            <th className="border p-2">Date</th>
                            <th className="border p-2">Amount</th>
                            <th className="border p-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border p-2">Tranche 1</td>
                            <td className="border p-2">15 Jan 2026</td>
                            <td className="border p-2">EUR 5,000,000</td>
                            <td className="border p-2">Disbursed</td>
                          </tr>
                          <tr>
                            <td className="border p-2">Tranche 2</td>
                            <td className="border p-2">15 Apr 2026</td>
                            <td className="border p-2">EUR 3,000,000</td>
                            <td className="border p-2">Pending</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                }
              />
              <Route
                path="lending/delinquent"
                element={<ComingSoon title="Delinquent Accounts" />}
              />
              <Route path="lending/analytics" element={<ComingSoon title="Lending Analytics" />} />
              <Route
                path="lending/alerts"
                element={<ComingSoon title="Alerts & Notifications" />}
              />

              {/* IFI Module - Sovereign & Country Risk Management */}
              <Route path="ifi" element={<CountryRiskPage />} />
              <Route path="ifi/countries" element={<CountryRiskPage />} />
              <Route
                path="ifi/holidays"
                element={<Navigate to="/officer/admin/holidays" replace />}
              />
              <Route path="ifi/compliance" element={<IFICompliancePage />} />
              <Route path="ifi/reporting" element={<IFIReportingPage />} />
              <Route path="ifi/guarantees" element={<GuaranteesPage />} />
              <Route path="ifi/guarantees/:id" element={<GuaranteeDetailPage />} />
              <Route path="ifi/projects" element={<ProjectsPage />} />
              <Route path="ifi/projects/new" element={<NewProjectPage />} />
              <Route path="ifi/projects/config" element={<ProjectConfigPage />} />
              <Route path="ifi/projects/:id" element={<ProjectDetailPage />} />
              <Route path="ifi/projects/:id/config" element={<ProjectConfigPage />} />
              <Route path="ifi/impact" element={<ImpactPage />} />
              <Route path="ifi/cofinancing" element={<CofinancingPage />} />
              <Route path="ifi/technical-assistance" element={<TechnicalAssistancePage />} />

              {/* Facilities Module - Multi-Tranche Credit Facilities */}
              <Route path="facilities" element={<FacilitiesPage />} />
              <Route path="facilities/new" element={<NewFacilityPage />} />
              <Route path="facilities/:id" element={<FacilityDetailPage />} />

              {/* Guarantor Management */}
              <Route path="guarantors" element={<GuarantorsPage />} />

              {/* Disbursements - redirect to lending disbursements */}
              <Route
                path="disbursements"
                element={<Navigate to="/officer/lending/disbursements" replace />}
              />
              <Route path="disbursements/new" element={<ComingSoon title="New Disbursement" />} />

              {/* Collateral Management */}
              <Route path="collateral" element={<CollateralPage />} />
              <Route path="collateral/:id" element={<CollateralDetailPage />} />

              {/* Pricing Module - V4.0 */}
              <Route path="pricing" element={<PricingDashboardPage />} />
              <Route path="pricing/calculate" element={<LoanPricingCalculatorPage />} />
              <Route
                path="pricing/calculate/:applicationId"
                element={<LoanPricingCalculatorPage />}
              />
              <Route path="pricing/scenarios" element={<ScenarioSimulatorPage />} />
              <Route path="pricing/history" element={<PricingHistoryPage />} />
              <Route path="pricing/rate-sheets" element={<RateSheetManagerPage />} />
              <Route path="pricing/market-data" element={<MarketDataPortal />} />
              <Route path="pricing/:id" element={<PricingDetailPage />} />

              <Route path="analytics" element={<DashboardPage />} />
              <Route path="analytics/advanced" element={<AdvancedAnalyticsPage />} />
              <Route path="analytics/risk" element={<RiskDashboardPage />} />
              <Route path="analytics/portfolio" element={<PortfolioAnalyticsPage />} />
              {/* Report Builder - V4.1 */}
              <Route path="analytics/reports" element={<ReportTemplatesPage />} />
              <Route path="analytics/reports/templates" element={<ReportTemplatesPage />} />
              <Route path="analytics/reports/builder" element={<ReportBuilderPage />} />
              <Route path="analytics/reports/builder/:id" element={<ReportBuilderPage />} />
              <Route path="analytics/reports/viewer" element={<ReportViewerPage />} />
              <Route path="analytics/reports/viewer/:id" element={<ReportViewerPage />} />

              {/* CRM Module - V4.0 */}
              <Route path="crm" element={<CRMDashboardPage />} />
              <Route path="crm/accounts" element={<AccountsPage />} />
              <Route path="crm/accounts/new" element={<NewAccountPage />} />
              <Route path="crm/accounts/:id" element={<AccountDetailPage />} />
              <Route path="crm/contacts" element={<ContactsPage />} />
              <Route path="crm/contacts/new" element={<NewContactPage />} />
              <Route path="crm/contacts/:id" element={<ContactDetailPage />} />
              <Route path="crm/contacts/:id/edit" element={<EditContactPage />} />
              <Route path="crm/leads" element={<LeadsPage />} />
              <Route path="crm/leads/new" element={<NewLeadPage />} />
              <Route path="crm/leads/:id" element={<LeadDetailPage />} />
              <Route path="crm/leads/:id/edit" element={<EditLeadPage />} />
              <Route path="crm/opportunities" element={<OpportunitiesPage />} />
              <Route path="crm/opportunities/pipeline" element={<OpportunitiesPipelinePage />} />
              <Route path="crm/opportunities/new" element={<NewOpportunityPage />} />
              <Route path="crm/opportunities/:id" element={<OpportunityDetailPage />} />
              <Route path="crm/opportunities/:id/edit" element={<EditOpportunityPage />} />
              <Route path="crm/pipeline" element={<PipelineStagesPage />} />
              <Route path="crm/pipeline/stages" element={<PipelineStagesPage />} />
              <Route path="crm/pipeline/forecast" element={<SalesForecastPage />} />
              <Route path="crm/activities" element={<ActivitiesPage />} />
              <Route path="crm/activities/new" element={<NewActivityPage />} />
              <Route path="crm/activities/calls" element={<ActivitiesPage />} />
              <Route path="crm/activities/meetings" element={<ActivitiesPage />} />
              <Route path="crm/activities/tasks" element={<ActivitiesPage />} />
              <Route path="crm/activities/:id" element={<ActivityDetailPage />} />
              <Route path="crm/activities/:id/edit" element={<EditActivityPage />} />
              <Route path="crm/campaigns" element={<CampaignsPage />} />
              <Route path="crm/campaigns/new" element={<NewCampaignPage />} />
              <Route path="crm/campaigns/:id" element={<ComingSoon title="Campaign Details" />} />
              <Route path="crm/term-sheets" element={<TermSheetsPage />} />
              <Route path="crm/term-sheets/new" element={<NewTermSheetPage />} />
              <Route path="crm/term-sheets/:id" element={<TermSheetDetailPage />} />
              <Route path="crm/term-sheets/:id/edit" element={<EditTermSheetPage />} />
              <Route path="crm/service-cases" element={<ServiceCasesPage />} />
              <Route path="crm/service-cases/new" element={<NewServiceCasePage />} />
              <Route
                path="crm/service-cases/:id"
                element={<ComingSoon title="Service Case Details" />}
              />
              <Route path="crm/analytics" element={<SalesPerformancePage />} />
              <Route path="crm/analytics/sales" element={<SalesPerformancePage />} />
              <Route path="crm/analytics/forecast" element={<SalesForecastPage />} />
              <Route path="crm/analytics/win-loss" element={<WinLossAnalysisPage />} />
              <Route path="crm/analytics/revenue-forecast" element={<RevenueForecastingPage />} />
              <Route
                path="crm/analytics/leads"
                element={<ComingSoon title="Lead Conversion Analytics" />}
              />
              <Route path="crm/analytics/campaigns" element={<ComingSoon title="Campaign ROI" />} />
              <Route path="crm/settings" element={<CRMSettingsPage />} />

              {/* Integration Portal - Unified into Officer Layout */}
              <Route path="integrations" element={<PortalDashboard />} />
              <Route path="integrations/dashboard" element={<PortalDashboard />} />
              <Route path="integrations/health" element={<HealthStatusPage />} />
              <Route path="integrations/activity" element={<ActivityFeedPage />} />
              <Route path="integrations/connectors" element={<InstalledConnectorsPage />} />
              <Route path="integrations/marketplace" element={<ConnectorMarketplace />} />
              <Route
                path="integrations/connectors/marketplace"
                element={<ConnectorMarketplace />}
              />
              <Route path="integrations/connectors/custom" element={<CustomConnectorBuilder />} />
              <Route path="integrations/connectors/:id" element={<ConnectorDetailView />} />
              <Route path="integrations/connectors/new" element={<CustomConnectorBuilder />} />
              <Route path="integrations/workflows" element={<IntegrationWorkflows />} />
              <Route
                path="integrations/workflows/new"
                element={<IntegrationWorkflows createNew />}
              />
              <Route path="integrations/workflows/:id" element={<IntegrationWorkflows />} />
              <Route
                path="integrations/business-workflows"
                element={<BusinessProcessWorkflows />}
              />
              <Route path="integrations/triggers" element={<TriggersPage />} />
              <Route path="integrations/actions" element={<ActionsPage />} />
              <Route path="integrations/api" element={<APIManagementDashboard />} />
              <Route path="integrations/api/keys" element={<ApiKeysPanel />} />
              <Route path="integrations/api/catalog" element={<APIManagementDashboard />} />
              <Route path="integrations/api/policies" element={<APIManagementDashboard />} />
              <Route path="integrations/api/analytics" element={<APIManagementDashboard />} />
              <Route path="integrations/api/developer" element={<DeveloperPortalHome />} />
              <Route path="integrations/api/developer/docs" element={<APIDocumentation />} />
              <Route path="integrations/api/developer/playground" element={<APIPlayground />} />
              <Route path="integrations/api/developer/sdks" element={<SDKDownloads />} />
              <Route
                path="integrations/api/developer/announcements"
                element={<AnnouncementsManagementPage />}
              />
              <Route path="integrations/developer" element={<DeveloperPortalHome />} />
              <Route path="integrations/developer/docs" element={<APIDocumentation />} />
              <Route path="integrations/developer/playground" element={<APIPlayground />} />
              <Route path="integrations/developer/sdks" element={<SDKDownloads />} />
              <Route path="integrations/webhooks" element={<WebhooksPage />} />
              <Route path="integrations/events" element={<EventLogPage />} />
              <Route path="integrations/exports" element={<ExportsPage />} />
              <Route path="integrations/schemas" element={<SchemasPage />} />
              <Route path="integrations/monitoring" element={<RealtimeMonitor />} />
              <Route path="integrations/monitoring/live" element={<RealtimeMonitor />} />
              <Route path="integrations/monitoring/realtime" element={<RealtimeMonitor />} />
              <Route path="integrations/monitoring/logs" element={<LogViewer />} />
              <Route path="integrations/monitoring/alerts" element={<AlertsPanel />} />
              <Route path="integrations/settings" element={<GeneralSettingsPage />} />
              <Route path="integrations/settings/team" element={<TeamSettingsPage />} />
              <Route path="integrations/settings/environments" element={<EnvironmentsPage />} />
              <Route
                path="integrations/settings/env-variables"
                element={<EnvironmentVariablesPage />}
              />

              <Route path="settings" element={<SettingsPage />} />

              {/* Documentation & Version History - Integrated into Officer Portal */}
              <Route path="docs" element={<DocumentationPage />} />
              <Route path="docs/getting-started/overview" element={<GettingStartedDoc />} />
              <Route path="docs/getting-started/*" element={<GettingStartedDoc />} />
              <Route path="docs/eod/overview" element={<EodOverviewDoc />} />
              <Route path="docs/eod/*" element={<EodOverviewDoc />} />
              <Route path="docs/pricing/overview" element={<PricingDoc />} />
              <Route path="docs/pricing/*" element={<PricingDoc />} />
              <Route path="docs/api/overview" element={<ApiOverviewDoc />} />
              <Route path="docs/api/auth" element={<AuthenticationDoc />} />
              <Route path="docs/api/connectors" element={<ConnectorsDoc />} />
              <Route path="docs/api/*" element={<ApiOverviewDoc />} />
              <Route path="docs/loan-origination/overview" element={<LoanOriginationDoc />} />
              <Route path="docs/compliance/overview" element={<ComplianceDoc />} />
              <Route path="docs/compliance/*" element={<ComplianceDoc />} />
              <Route path="docs/underwriting/overview" element={<UnderwritingDoc />} />
              <Route path="docs/underwriting/*" element={<UnderwritingDoc />} />
              <Route path="docs/servicing/overview" element={<ServicingDoc />} />
              <Route path="docs/servicing/*" element={<ServicingDoc />} />
              <Route path="docs/origination/application" element={<LoanOriginationDoc />} />
              <Route path="docs/origination/borrowers" element={<BorrowerManagementDoc />} />
              <Route path="docs/origination/documents" element={<DocumentManagementDoc />} />
              <Route path="docs/origination/ubo" element={<UBODoc />} />
              <Route path="docs/disbursement/process" element={<DisbursementDoc />} />
              <Route path="docs/disbursement/*" element={<DisbursementDoc />} />
              <Route path="docs/admin/storage" element={<StorageConfigurationDoc />} />
              <Route path="docs/agent/overview" element={<AIAgentDoc />} />
              <Route path="docs/agent/*" element={<AIAgentDoc />} />
              <Route path="docs/admin/*" element={<DocumentationPage />} />
              <Route path="docs/analytics/*" element={<DocumentationPage />} />
              <Route path="docs/officer/*" element={<DocumentationPage />} />
              <Route path="docs/*" element={<DocumentationPage />} />
              <Route path="version-history" element={<VersionHistoryPage />} />
            </Route>

            {/* Legacy Integration Portal - Redirect to unified Officer layout, preserving sub-paths */}
            <Route path="/integration-portal/*" element={<IntegrationPortalRedirect />} />

            {/* Admin Portal - Standalone admin routes for specific admin functions */}
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <Suspense fallback={<PageLoader />}>
                    <OfficerLayout />
                  </Suspense>
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/officer/admin" replace />} />
              {/* FX Management - Dashboard and Exposure pages */}
              <Route path="fx" element={<FxExposurePage />} />
              <Route path="fx/dashboard" element={<FxExposurePage />} />
              <Route path="fx/exposure" element={<FxExposurePage />} />
              <Route path="fx/performance" element={<FxExposurePage />} />
              <Route path="fx/revaluation" element={<FxRevaluationPage />} />
              <Route path="fx/rates" element={<FxRateSettingsPage />} />
              <Route path="fx/rates/history" element={<FxRateHistoryPage />} />
              <Route
                path="fx/counterparties"
                element={<ComingSoon title="Counterparty Management" />}
              />
              <Route path="fx/limits" element={<ComingSoon title="Hedge Limits" />} />
              {/* Analytics */}
              <Route path="analytics" element={<Navigate to="/officer/analytics" replace />} />
              <Route path="analytics/portfolio" element={<PortfolioAnalyticsPage />} />
              <Route path="analytics/fx-exposure" element={<FxExposurePage />} />
              {/* Servicing */}
              <Route
                path="servicing"
                element={<Navigate to="/officer/servicing/prepayments" replace />}
              />
              <Route path="servicing/prepayments" element={<PrepaymentApprovalPage />} />
              <Route path="servicing/restructuring" element={<RestructuringListPage />} />
              <Route path="servicing/write-offs" element={<WriteOffsPage />} />
              {/* Reports */}
              <Route path="reports/write-offs" element={<WriteOffsPage />} />
              {/* GL */}
              <Route path="gl/postings" element={<GLAccountingPage />} />
              {/* Settings */}
              <Route path="settings/fx-rates" element={<FxRateSettingsPage />} />
              <Route
                path="settings/fx-hedging"
                element={<ComingSoon title="FX Hedging Settings" />}
              />
              {/* Admin page redirects - E2E tests navigate to /admin/* paths */}
              <Route
                path="collateral"
                element={<Navigate to="/officer/admin/collateral" replace />}
              />
              <Route path="dashboard" element={<Navigate to="/officer/admin" replace />} />
            </Route>

            {/* Legacy Routes - Redirect to Portals */}
            <Route path="/dashboard" element={<PortalSelectionPage />} />
            <Route path="/applications" element={<PortalSelectionPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <Toaster />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
